import {ReactElement, useContext, useEffect, useState} from 'react';
import {getBudgetYearForProperty, useProperties} from '../../../../contexts/properties/PropertiesContext';

import {ThemeProvider} from "styled-components";
import {ViziblyTheme} from "../../../../styles/zendesk-garden/ViziblyZDGTheme";

import {Tabs} from "@zendeskgarden/react-tabs";

import * as workflowCSS from "../../../../styles/workflows/workflowCSS.module.scss";
import * as workflowHeaderCSS from "../../../../components/workflows/workflow-header/styles/css.module.scss";

import WorkflowHeader from "../../../../components/workflows/workflow-header/WorkflowHeader";
import WorkflowNavToggle from "../../../../components/workflows/workflow-nav-toggle/WorkflowNavToggle";
import WorkflowPageLabel from "../../../../components/workflows/workflow-page-label/WorkflowPageLabel";
import WorkflowStepNav from "../../../../components/workflows/workflow-step-nav/WorkflowStepNav";

import WorkflowYearTabs, {
    WORKFLOW_YEAR_TAB_BUDGET
} from "../../../../components/workflows/workflow-year-tabs/WorkflowYearTabs";
import {IWorkflowPageProps, shouldUpdateWorkflowTab} from "../../logic/workflows";
import {useParams} from "react-router-dom";
import {BudgetingType} from "../../../../BudgetingType";
import {useSettings} from "../../../../contexts/settings/SettingsContext";
import { OriginalRevenueRenewalIncreaseType, useGetForecastLocksLazyQuery, useGetOriginalRevenuePropertyRenewalIncreasePercentLazyQuery, useSetOriginalRevenuePropertyRenewalIncreasePercentMutation, VersionType } from '../../../../__generated__/generated_types';
import { RenewalIncreaseConfiguration } from "./RenewalIncreaseConfiguration";
import { toast } from "react-toastify";
import RenewalRateManualEntryTable from './RenewalRateManualEntryTable';
import RenewalRateGrowthTable from './RenewalRateGrowthTable';
import VZSaveStatus from '../../../../components/vz-ui/vz-save-status/VZSaveStatus';
import { OperationalFinancialMetrics } from '../operational-metrics';
import {AuthContext} from "../../../../contexts/AuthContext";


export default function RenewalRates(props:IWorkflowPageProps): ReactElement {
    const auth = useContext(AuthContext);
    const { type } = useParams() as { type: BudgetingType; propertyId: string };
    const { year } = useSettings();
    const { currentProperty, loaded: propertyContextLoaded } = useProperties();
    const budgetYear = getBudgetYearForProperty(currentProperty, auth.budgetYear);

    const currentVersionYear = type == BudgetingType.BUDGET ? year + 1 : year;
    const versionType = type == BudgetingType.BUDGET ? VersionType.Budget : VersionType.Reforecast;
    const [lastSaved, setLastSaved] = useState<Date>(new Date());
    const [savingValues, setSavingValues] = useState<boolean>(false);

    const [getForecastLocks, {data: forecastLocks, loading: lockDataLoading}] = useGetForecastLocksLazyQuery({
        fetchPolicy: "no-cache",
    });

    const [getRenewalIncreaseConfiguration, {data: renewalIncreaseConfigurationData}] = useGetOriginalRevenuePropertyRenewalIncreasePercentLazyQuery({
        fetchPolicy: "no-cache",
    });

    const [saveRenewalIncreaseConfiguration] = useSetOriginalRevenuePropertyRenewalIncreasePercentMutation({
        notifyOnNetworkStatusChange: true,
        onCompleted: data => {
            if (data?.setOriginalRevenuePropertyRenewalIncreasePercent === true) {
                toast.success(`Updated Successfully`);
            }
            else {
                toast.error(`Failed To Update`);
            }
            refetchRenewalIncreaseConfiguration();
        },
    });

    function refetchRenewalIncreaseConfiguration() {
        if (currentProperty) {
            getRenewalIncreaseConfiguration({
                variables: {
                    propertyId: currentProperty.id,
                    budgetYear: currentProperty.budgetYear
                }
            });
        }
    }

    function handleSaveRenewalIncreaseConfiguration(type: OriginalRevenueRenewalIncreaseType | null, percent: number | null, customFormula: string | null) {
        if (!currentProperty) {
            return;
        }
        saveRenewalIncreaseConfiguration({
            variables: {
                propertyId: currentProperty.id,
                budgetYear: currentProperty.budgetYear,
                type,
                percent,
                customFormula
            }
        });
    }

    useEffect(() => {
        if (!currentProperty?.id) {
            return;
        }

        getForecastLocks({
            variables: {
                propertyId: currentProperty.id,
                budgetYear: year + 1,
            }
        });
    }, [currentProperty]);

    useEffect(() => {
        if(!currentProperty || !propertyContextLoaded) {
            return;
        }
        refetchRenewalIncreaseConfiguration();
    }, [currentProperty, propertyContextLoaded, type]);

    useEffect(
            () => {
                const updateWorkflowTab = shouldUpdateWorkflowTab(type, selectedTab);
                if(updateWorkflowTab != false){
                    setSelectedTab(updateWorkflowTab as string);
                }
            },
            [type]
    );

    const [selectedTab, setSelectedTab] = useState(WORKFLOW_YEAR_TAB_BUDGET);

    return (
        <ThemeProvider theme={ViziblyTheme}>
            <div className={`${workflowCSS.workflowContainer} ${workflowCSS.revenueScreen}`}>
                <WorkflowHeader>
                    <WorkflowHeader.LeftCol>
                        <div className={workflowHeaderCSS.rowItemsContainer}>
                            <WorkflowNavToggle inUserWorkflow={props.inUserWorkflow}/>
                            <WorkflowPageLabel label={renewalIncreaseConfigurationData?.originalRevenuePropertyRenewalIncreasePercent?.type ? 'Renewal Tradeout' : 'Renewal Increase'}/>
                            {renewalIncreaseConfigurationData &&
                            <RenewalIncreaseConfiguration
                                disabled={forecastLocks?.forecastLocks?.property?.reforecastLocked || forecastLocks?.forecastLocks?.property?.budgetLocked}
                                type={renewalIncreaseConfigurationData.originalRevenuePropertyRenewalIncreasePercent.type ?? null}
                                percent={renewalIncreaseConfigurationData.originalRevenuePropertyRenewalIncreasePercent.percent ?? null}
                                customFormula={renewalIncreaseConfigurationData.originalRevenuePropertyRenewalIncreasePercent.customFormula ?? null}
                                onSave={handleSaveRenewalIncreaseConfiguration}
                            />
                            }
                        </div>
                    </WorkflowHeader.LeftCol>
                    <WorkflowHeader.RightCol>
                        <VZSaveStatus isSaving={savingValues} lastSaved={lastSaved} />
                        <WorkflowStepNav onPreviousClick={props.onPreviousClick} onNextClick={props.onNextClick} inUserWorkflow={props.inUserWorkflow}/>
                    </WorkflowHeader.RightCol>
                </WorkflowHeader>

                <div className={workflowCSS.bodyContainer}>
                    { currentProperty &&
                        <>
                            <Tabs selectedItem={selectedTab} onChange={setSelectedTab} className={workflowCSS.tabsContainer}>
                                <WorkflowYearTabs
                                    property={currentProperty}
                                    selectedTab={selectedTab}
                                    rawForecastLocks={forecastLocks}
                                />
                            </Tabs>

                            <div className={workflowCSS.tableWrapper}>
                                { renewalIncreaseConfigurationData && renewalIncreaseConfigurationData.originalRevenuePropertyRenewalIncreasePercent.type !== null ?
                                    <RenewalRateGrowthTable
                                        property={currentProperty}
                                        budgetYear={budgetYear}
                                        versionType={versionType}
                                    />
                                    :
                                    <RenewalRateManualEntryTable
                                        property={currentProperty}
                                        budgetYear={budgetYear}
                                        year={currentVersionYear}
                                        versionType={versionType}
                                        onLastSaved={setLastSaved}
                                        onSavingValues={setSavingValues}
                                    />
                                }
                                {!currentProperty?.isAcquisitionProperty &&
                                    <OperationalFinancialMetrics type={type} budgetYear={budgetYear}/>
                                }
                            </div>
                        </>
                    }
                </div>
            </div>
        </ThemeProvider>
    );
}
